/**
 * INTERVIEW SCORE — the question-level evaluation (0105), the top panel of the review page.
 *
 * Every answered question is scored 0-100 from the role and that question alone — no competencies — and the
 * counted scores average into one number. It is a different measurement from the competency score below it:
 * the two are never blended, and which one a list ranks by is the organization's choice (shown as a chip).
 * Content only: accent, tone and fluency are never scored.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { INTERVIEW_CLASS_LABEL, RANKING_BASIS_LABEL, type InterviewClass, type QuestionScore, type RankingBasis } from "@talenthop/shared";
import { api, ApiError } from "../../lib/api.js";
import { Info } from "../../components/icons.js";

export interface InterviewScoreData {
  score: number | null;
  scored: number;
  total: number;
  class: InterviewClass;
  label: string;
  scores: QuestionScore[];
  pending: boolean;
  scoredAt: string | null;
}

const tone = (score: number | null): string => (score == null ? "n" : score >= 70 ? "hi" : score >= 40 ? "mid" : "low");
const SOURCE_NOTE: Record<string, string> = { code_run: "from the test results", snippet_pick: "from the option chosen" };

export function InterviewScorePanel({
  interview,
  rankingBasis,
  promptBySeq,
  candidacyId,
  canScore,
  onQueued,
  bare,
  listHref,
}: {
  interview: InterviewScoreData;
  /** Absent on a shared link: another organization's ranking choice is not that reader's business. */
  rankingBasis?: RankingBasis;
  /** Pass to list every question under the headline — only where there is no Interview assistant (the shared report). */
  promptBySeq?: Map<number, string>;
  candidacyId: string;
  /** The owning org's editor may (re)score; a fulfilling vendor may not. */
  canScore: boolean;
  onQueued: () => void;
  /** No card or title: the headline (ring, verdict, chips, re-score) only, to sit at the top of the Interview assistant. */
  bare?: boolean;
  /** The candidate list this ranking applies to (this requirement's Candidates page) — makes the
   *  "Lists rank by …" chip a link there. Absent (shared link) → plain text. */
  listHref?: string;
}): ReactNode {
  const queue = useMutation({
    mutationFn: () => api.post(`/candidacies/${candidacyId}/question-scores`, {}),
    onSuccess: onQueued,
  });
  const hasScores = interview.score !== null;
  const limited = interview.class === "limited";
  const meta = (
    <span className="t1-iv-meta">
      {rankingBasis === undefined ? null : listHref ? (
        <Link
          to={listHref}
          className="pill t t1-iv-listlink"
          data-tip={`Open this requirement's candidates — ranked by the ${rankingBasis === "interview" ? "interview" : RANKING_BASIS_LABEL[rankingBasis].toLowerCase()} score`}
          data-tip-pos="top"
        >
          {rankingBasis === "interview" ? "Lists rank by this" : `Lists rank by ${RANKING_BASIS_LABEL[rankingBasis].toLowerCase()}`} →
        </Link>
      ) : rankingBasis === "interview" ? (
        <span className="pill t" title="This organization ranks candidate lists by the interview score.">
          Lists rank by this
        </span>
      ) : (
        <span className="mut sm" title="This organization ranks candidate lists by the competency score.">
          Lists rank by {RANKING_BASIS_LABEL[rankingBasis].toLowerCase()}
        </span>
      )}
      <span
        title="Each answered question is scored 0–100 from the role and that question alone, then the scores are averaged. It is separate from the competency score below. Accent, tone and fluency are never scored. A human decides."
        style={{ display: "inline-flex", width: 13, color: "var(--ink-mute)", cursor: "help" }}
      >
        <Info />
      </span>
    </span>
  );
  const body = (
    <div className="card-b">
      <div className="t1-iv-head">
        <span className={`t1-ring lg ${tone(interview.score) === "n" ? "" : tone(interview.score)}`} style={{ ["--v" as string]: interview.score ?? 0 }}>
          <span>{interview.score ?? "—"}</span>
        </span>
        <div>
          <div className={`t1-rv-kpi-tone ${hasScores ? (limited ? "r" : interview.class === "strong" ? "v" : interview.class === "weak" ? "f" : "r") : ""}`}>
            {hasScores ? INTERVIEW_CLASS_LABEL[interview.class] : interview.pending ? "Scoring in progress" : INTERVIEW_CLASS_LABEL.none}
          </div>
          <div className="t1-rv-kpi-d">
            {hasScores ? `${interview.scored} of ${interview.total} questions scored` : "No question has a score yet"}
            {limited && " — too few answers scored to rely on the average"}
          </div>
        </div>
        {canScore && (
          <button type="button" className="btn sm ghost t1-iv-act" disabled={queue.isPending || interview.pending} onClick={() => queue.mutate()}>
            {interview.pending || queue.isSuccess ? "Queued…" : hasScores ? "Re-score questions" : "Score questions"}
          </button>
        )}
      </div>
      {queue.isError && (
        <p className="sm" style={{ color: "var(--crit)", margin: "8px 0 0" }}>
          {queue.error instanceof ApiError ? queue.error.message : "Couldn't queue scoring."}
        </p>
      )}
      {promptBySeq && interview.scores.length > 0 && (
        <ul className="t1-iv-list">
          {interview.scores.map((q) => (
            <li key={q.questionId}>
              <button
                type="button"
                className="t1-iv-row"
                onClick={() => document.getElementById(`rv-q-${q.seq}`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
                title="Jump to this answer in the transcript"
              >
                <span className="t1-iv-q">Q{q.seq}</span>
                <span className="t1-iv-body">
                  <span className="t1-iv-prompt">{promptBySeq?.get(q.seq) ?? "Question"}</span>
                  <span className="mut sm">
                    {q.reason}
                    {SOURCE_NOTE[q.source] ? ` (${SOURCE_NOTE[q.source]})` : ""}
                    {!q.counts && " · not counted in the average"}
                  </span>
                </span>
                <span className="t1-iv-score">
                  <b className="tnum">{q.score ?? "—"}</b>
                  <span className="t1-bt">
                    <i className={q.score == null ? "" : q.score >= 70 ? "" : q.score >= 40 ? "mid" : "low"} style={{ width: `${q.score ?? 0}%` }} />
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!hasScores && !interview.pending && (
        <p className="mut sm" style={{ margin: "10px 0 0" }}>
          Questions are scored once the interview is complete. Older interviews are scored in the background, or use “Score questions”.
        </p>
      )}
    </div>
  );
  if (bare) {
    return (
      <div className="t1-iv t1-iv-bare" aria-label="Interview score">
        <div className="t1-iv-bare-h">
          <b>Interview score</b>
          {meta}
        </div>
        {body}
      </div>
    );
  }
  return (
    <div className="card t1-iv" aria-label="Interview score">
      <div className="card-h">
        <h3>Interview score</h3>
        {meta}
      </div>
      {body}
    </div>
  );
}
